import React, { useId } from 'react';
import { Box } from '@mui/material';

// Small area chart. Points with source "operational" (not yet covered by WB's financial
// reports) are drawn dashed; "finance" and "orders" (Ozon's daily ordered amount) are solid.
// Days without data are gaps rather than invented zeros.
const W = 320;

function path(points, h, pad) {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
}

const Sparkline = ({ data = [], color, height = 72, label }) => {
  const id = useId().replace(/:/g, '');
  const values = data.filter((d) => d.source !== 'none').map((d) => Number(d.value) || 0);
  if (values.length < 2) {
    return <Box sx={{ height }} aria-hidden />;
  }
  const min = Math.min(0, ...values);
  const max = Math.max(...values, 1);
  const pad = 4;
  const step = data.length > 1 ? W / (data.length - 1) : W;
  const y = (v) => pad + (height - pad * 2) * (1 - (v - min) / (max - min || 1));
  const pts = data.map((d, i) => ({ x: i * step, y: y(Number(d.value) || 0), source: d.source }));

  const finance = pts.filter((p) => p.source === 'finance' || p.source === 'orders');
  const firstOp = pts.findIndex((p) => p.source === 'operational');
  const opPts = firstOp >= 0 ? pts.slice(Math.max(0, firstOp - 1)).filter((p) => p.source !== 'none') : [];
  const solid = finance.length > 1 ? finance : [];
  const areaBase = solid.length > 1 ? `${path(solid)} L${solid[solid.length - 1].x},${height} L${solid[0].x},${height} Z` : '';

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      sx={{ width: '100%', height, display: 'block', overflow: 'visible' }}
    >
      <defs>
        <linearGradient id={`g${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {areaBase && <path d={areaBase} fill={`url(#g${id})`} />}
      {solid.length > 1 && (
        <path d={path(solid)} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      )}
      {opPts.length > 1 && (
        <path d={path(opPts)} fill="none" stroke={color} strokeWidth="2" strokeDasharray="4 4" strokeOpacity="0.7" vectorEffect="non-scaling-stroke" />
      )}
    </Box>
  );
};

export default Sparkline;
